/**
 * Cache Context & Hook
 * =====================
 * React context for managing cached data with chunked loading.
 * Provides easy-to-use hooks for components to access cached data.
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import apiService from '../services/api';
import cacheService from '../services/cacheService';

// =====================================================
// INITIAL STATE
// =====================================================

const initialState = {
  // Communities
  communityCategories: [],
  communitiesByCategory: {},  // { categoryId: { items: [], hasMore: true, loading: false } }
  selectedCommunityCategory: 'all',
  
  // Free Agents
  agentCategories: [],
  agentsByCategory: {},       // { categoryId: { items: [], hasMore: true, loading: false } }
  selectedAgentCategory: 'all',
  
  // My Groups
  groupTypes: [],
  groupsByType: {},           // { typeId: { items: [], hasMore: true, loading: false } }
  selectedGroupType: 'all',
  
  // Loading states
  loadingCategories: false,
  error: null,
};

// =====================================================
// ACTION TYPES
// =====================================================

const ACTIONS = {
  // Categories
  SET_COMMUNITY_CATEGORIES: 'SET_COMMUNITY_CATEGORIES',
  SET_AGENT_CATEGORIES: 'SET_AGENT_CATEGORIES',
  SET_GROUP_TYPES: 'SET_GROUP_TYPES',
  
  // Data
  SET_COMMUNITIES: 'SET_COMMUNITIES',
  APPEND_COMMUNITIES: 'APPEND_COMMUNITIES',
  SET_AGENTS: 'SET_AGENTS',
  APPEND_AGENTS: 'APPEND_AGENTS',
  SET_GROUPS: 'SET_GROUPS',
  APPEND_GROUPS: 'APPEND_GROUPS',
  
  // Selection
  SELECT_COMMUNITY_CATEGORY: 'SELECT_COMMUNITY_CATEGORY',
  SELECT_AGENT_CATEGORY: 'SELECT_AGENT_CATEGORY',
  SELECT_GROUP_TYPE: 'SELECT_GROUP_TYPE',
  
  // Loading
  SET_LOADING: 'SET_LOADING',
  SET_CATEGORY_LOADING: 'SET_CATEGORY_LOADING',
  SET_ERROR: 'SET_ERROR',
  
  // Cache
  CLEAR_CACHE: 'CLEAR_CACHE',
};

// =====================================================
// REDUCER
// =====================================================

function cacheReducer(state, action) {
  switch (action.type) {
    // Categories
    case ACTIONS.SET_COMMUNITY_CATEGORIES:
      return { ...state, communityCategories: action.payload, loadingCategories: false };
    
    case ACTIONS.SET_AGENT_CATEGORIES:
      return { ...state, agentCategories: action.payload };
    
    case ACTIONS.SET_GROUP_TYPES:
      return { ...state, groupTypes: action.payload };
    
    // Communities
    case ACTIONS.SET_COMMUNITIES:
      return {
        ...state,
        communitiesByCategory: {
          ...state.communitiesByCategory,
          [action.payload.categoryId]: {
            items: action.payload.items,
            hasMore: action.payload.hasMore,
            loading: false,
          },
        },
      };
    
    case ACTIONS.APPEND_COMMUNITIES:
      const existingCommunities = state.communitiesByCategory[action.payload.categoryId]?.items || [];
      return {
        ...state,
        communitiesByCategory: {
          ...state.communitiesByCategory,
          [action.payload.categoryId]: {
            items: [...existingCommunities, ...action.payload.items],
            hasMore: action.payload.hasMore,
            loading: false,
          },
        },
      };
    
    // Agents
    case ACTIONS.SET_AGENTS:
      return {
        ...state,
        agentsByCategory: {
          ...state.agentsByCategory,
          [action.payload.categoryId]: {
            items: action.payload.items,
            hasMore: action.payload.hasMore,
            loading: false,
          },
        },
      };
    
    case ACTIONS.APPEND_AGENTS:
      const existingAgents = state.agentsByCategory[action.payload.categoryId]?.items || [];
      return {
        ...state,
        agentsByCategory: {
          ...state.agentsByCategory,
          [action.payload.categoryId]: {
            items: [...existingAgents, ...action.payload.items],
            hasMore: action.payload.hasMore,
            loading: false,
          },
        },
      };
    
    // Groups
    case ACTIONS.SET_GROUPS:
      return {
        ...state,
        groupsByType: {
          ...state.groupsByType,
          [action.payload.typeId]: {
            items: action.payload.items,
            hasMore: action.payload.hasMore,
            loading: false,
          },
        },
      };
    
    case ACTIONS.APPEND_GROUPS:
      const existingGroups = state.groupsByType[action.payload.typeId]?.items || [];
      return {
        ...state,
        groupsByType: {
          ...state.groupsByType,
          [action.payload.typeId]: {
            items: [...existingGroups, ...action.payload.items],
            hasMore: action.payload.hasMore,
            loading: false,
          },
        },
      };
    
    // Selection
    case ACTIONS.SELECT_COMMUNITY_CATEGORY:
      return { ...state, selectedCommunityCategory: action.payload };
    
    case ACTIONS.SELECT_AGENT_CATEGORY:
      return { ...state, selectedAgentCategory: action.payload };
    
    case ACTIONS.SELECT_GROUP_TYPE:
      return { ...state, selectedGroupType: action.payload };
    
    // Loading
    case ACTIONS.SET_LOADING:
      return { ...state, loadingCategories: action.payload };
    
    case ACTIONS.SET_CATEGORY_LOADING: {
      const { type, categoryId, loading } = action.payload;
      const stateKey = type === 'communities' ? 'communitiesByCategory' 
                     : type === 'agents' ? 'agentsByCategory' 
                     : 'groupsByType';
      return {
        ...state,
        [stateKey]: {
          ...state[stateKey],
          [categoryId]: {
            ...state[stateKey][categoryId],
            loading,
          },
        },
      };
    }
    
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    
    case ACTIONS.CLEAR_CACHE:
      return initialState;
    
    default:
      return state;
  }
}

// =====================================================
// CONTEXT
// =====================================================

const CacheContext = createContext(null);

export function CacheProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [state, dispatch] = useReducer(cacheReducer, initialState);
  
  // Initialize cache when user changes
  useEffect(() => {
    if (user?.id) {
      cacheService.initializeCache(user.id);
    } else {
      cacheService.clearCache();
      dispatch({ type: ACTIONS.CLEAR_CACHE });
    }
  }, [user?.id]);
  
  // =====================================================
  // COMMUNITY ACTIONS
  // =====================================================
  
  const loadCommunityCategories = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const categories = await cacheService.getCommunityCategories(async () => {
        const res = await apiService.getCommunityCategories?.() || { categories: [] };
        return res.categories || [
          { id: 'all', name: 'All Communities', icon: '🌐' },
          { id: 'gaming', name: 'Gaming', icon: '🎮' },
          { id: 'art', name: 'Art & Design', icon: '🎨' },
          { id: 'music', name: 'Music', icon: '🎵' },
          { id: 'tech', name: 'Technology', icon: '💻' },
          { id: 'education', name: 'Education', icon: '📚' },
          { id: 'lifestyle', name: 'Lifestyle', icon: '🌟' },
        ];
      });
      dispatch({ type: ACTIONS.SET_COMMUNITY_CATEGORIES, payload: categories });
    } catch (e) {
      console.error('Failed to load community categories:', e);
      dispatch({ type: ACTIONS.SET_ERROR, payload: e.message });
    }
  }, []);
  
  const loadCommunitiesByCategory = useCallback(async (categoryId = 'all', forceRefresh = false) => {
    dispatch({ 
      type: ACTIONS.SET_CATEGORY_LOADING, 
      payload: { type: 'communities', categoryId, loading: true } 
    });
    
    try {
      const result = await cacheService.getCommunitiesByCategory(
        categoryId === 'all' ? null : categoryId,
        async ({ categoryId: catId, limit, offset }) => {
          const params = { limit, offset };
          if (catId) params.category = catId;
          return await apiService.getCommunities(params);
        },
        { forceRefresh }
      );
      
      dispatch({
        type: ACTIONS.SET_COMMUNITIES,
        payload: { categoryId, items: result.items, hasMore: result.hasMore },
      });
    } catch (e) {
      console.error('Failed to load communities:', e);
      dispatch({ type: ACTIONS.SET_ERROR, payload: e.message });
    }
  }, []);
  
  const loadMoreCommunitiesForCategory = useCallback(async (categoryId = 'all') => {
    dispatch({ 
      type: ACTIONS.SET_CATEGORY_LOADING, 
      payload: { type: 'communities', categoryId, loading: true } 
    });
    
    try {
      const result = await cacheService.loadMoreCommunities(
        categoryId === 'all' ? null : categoryId,
        async ({ categoryId: catId, limit, offset }) => {
          const params = { limit, offset };
          if (catId) params.category = catId;
          return await apiService.getCommunities(params);
        }
      );
      
      if (result.loadedMore) {
        dispatch({
          type: ACTIONS.APPEND_COMMUNITIES,
          payload: { categoryId, items: result.newItems, hasMore: result.hasMore },
        });
      }
    } catch (e) {
      console.error('Failed to load more communities:', e);
    }
  }, []);
  
  const selectCommunityCategory = useCallback((categoryId) => {
    dispatch({ type: ACTIONS.SELECT_COMMUNITY_CATEGORY, payload: categoryId });
    // Load data for the category if not already loaded
    if (!state.communitiesByCategory[categoryId]?.items?.length) {
      loadCommunitiesByCategory(categoryId);
    }
  }, [loadCommunitiesByCategory, state.communitiesByCategory]);
  
  // =====================================================
  // AGENT ACTIONS
  // =====================================================
  
  const loadAgentCategories = useCallback(async () => {
    try {
      const categories = await cacheService.getFreeAgentCategories(async () => {
        const res = await apiService.getAgentCategories?.() || { categories: [] };
        return res.categories || [
          { id: 'all', name: 'All Agents', icon: '👥' },
          { id: 'developers', name: 'Developers', icon: '💻' },
          { id: 'designers', name: 'Designers', icon: '🎨' },
          { id: 'writers', name: 'Writers', icon: '✍️' },
          { id: 'musicians', name: 'Musicians', icon: '🎵' },
          { id: 'video', name: 'Video Creators', icon: '🎬' },
        ];
      });
      dispatch({ type: ACTIONS.SET_AGENT_CATEGORIES, payload: categories });
    } catch (e) {
      console.error('Failed to load agent categories:', e);
    }
  }, []);
  
  const loadAgentsByCategory = useCallback(async (categoryId = 'all', forceRefresh = false) => {
    dispatch({ 
      type: ACTIONS.SET_CATEGORY_LOADING, 
      payload: { type: 'agents', categoryId, loading: true } 
    });
    
    try {
      const result = await cacheService.getFreeAgentsByCategory(
        categoryId === 'all' ? null : categoryId,
        async ({ categoryId: catId, limit, offset }) => {
          const params = { limit, offset };
          if (catId) params.category = catId;
          return await apiService.getFreeAgents?.(params) || { users: [] };
        },
        { forceRefresh }
      );
      
      dispatch({
        type: ACTIONS.SET_AGENTS,
        payload: { categoryId, items: result.items, hasMore: result.hasMore },
      });
    } catch (e) {
      console.error('Failed to load agents:', e);
    }
  }, []);
  
  const loadMoreAgentsForCategory = useCallback(async (categoryId = 'all') => {
    dispatch({ 
      type: ACTIONS.SET_CATEGORY_LOADING, 
      payload: { type: 'agents', categoryId, loading: true } 
    });
    
    try {
      const result = await cacheService.loadMoreFreeAgents(
        categoryId === 'all' ? null : categoryId,
        async ({ categoryId: catId, limit, offset }) => {
          const params = { limit, offset };
          if (catId) params.category = catId;
          return await apiService.getFreeAgents?.(params) || { users: [] };
        }
      );
      
      if (result.loadedMore) {
        dispatch({
          type: ACTIONS.APPEND_AGENTS,
          payload: { categoryId, items: result.newItems, hasMore: result.hasMore },
        });
      }
    } catch (e) {
      console.error('Failed to load more agents:', e);
    }
  }, []);
  
  const selectAgentCategory = useCallback((categoryId) => {
    dispatch({ type: ACTIONS.SELECT_AGENT_CATEGORY, payload: categoryId });
    if (!state.agentsByCategory[categoryId]?.items?.length) {
      loadAgentsByCategory(categoryId);
    }
  }, [loadAgentsByCategory, state.agentsByCategory]);
  
  // =====================================================
  // GROUP ACTIONS
  // =====================================================
  
  const loadGroupTypes = useCallback(async () => {
    try {
      const types = await cacheService.getGroupTypes(async () => {
        return [
          { id: 'all', name: 'All Groups', icon: '📁' },
          { id: 'owned', name: 'My Groups', icon: '👑' },
          { id: 'member', name: 'Joined', icon: '🤝' },
          { id: 'invited', name: 'Invitations', icon: '💌' },
        ];
      });
      dispatch({ type: ACTIONS.SET_GROUP_TYPES, payload: types });
    } catch (e) {
      console.error('Failed to load group types:', e);
    }
  }, []);
  
  const loadGroupsByType = useCallback(async (typeId = 'all', forceRefresh = false) => {
    if (!user?.id) return;
    
    dispatch({ 
      type: ACTIONS.SET_CATEGORY_LOADING, 
      payload: { type: 'groups', categoryId: typeId, loading: true } 
    });
    
    try {
      const result = await cacheService.getMyGroupsByType(
        typeId === 'all' ? null : typeId,
        async ({ typeId: tId, limit, offset }) => {
          const params = { limit, offset };
          if (tId) params.type = tId;
          return await apiService.getUserCommunities(user.id, params);
        },
        { forceRefresh }
      );
      
      dispatch({
        type: ACTIONS.SET_GROUPS,
        payload: { typeId, items: result.items, hasMore: result.hasMore },
      });
    } catch (e) {
      console.error('Failed to load groups:', e);
    }
  }, [user?.id]);
  
  const loadMoreGroupsForType = useCallback(async (typeId = 'all') => {
    if (!user?.id) return;
    
    dispatch({ 
      type: ACTIONS.SET_CATEGORY_LOADING, 
      payload: { type: 'groups', categoryId: typeId, loading: true } 
    });
    
    try {
      const result = await cacheService.loadMoreGroups(
        typeId === 'all' ? null : typeId,
        async ({ typeId: tId, limit, offset }) => {
          const params = { limit, offset };
          if (tId) params.type = tId;
          return await apiService.getUserCommunities(user.id, params);
        }
      );
      
      if (result.loadedMore) {
        dispatch({
          type: ACTIONS.APPEND_GROUPS,
          payload: { typeId, items: result.newItems, hasMore: result.hasMore },
        });
      }
    } catch (e) {
      console.error('Failed to load more groups:', e);
    }
  }, [user?.id]);
  
  const selectGroupType = useCallback((typeId) => {
    dispatch({ type: ACTIONS.SELECT_GROUP_TYPE, payload: typeId });
    if (!state.groupsByType[typeId]?.items?.length) {
      loadGroupsByType(typeId);
    }
  }, [loadGroupsByType, state.groupsByType]);
  
  // =====================================================
  // CACHE MANAGEMENT
  // =====================================================
  
  const refreshAll = useCallback(() => {
    cacheService.clearCache();
    dispatch({ type: ACTIONS.CLEAR_CACHE });
    loadCommunityCategories();
    loadAgentCategories();
    loadGroupTypes();
  }, [loadCommunityCategories, loadAgentCategories, loadGroupTypes]);
  
  const getCacheStats = useCallback(() => {
    return cacheService.getCacheStats();
  }, []);
  
  // =====================================================
  // CONTEXT VALUE
  // =====================================================
  
  const value = {
    // State
    ...state,
    
    // Community actions
    loadCommunityCategories,
    loadCommunitiesByCategory,
    loadMoreCommunitiesForCategory,
    selectCommunityCategory,
    
    // Agent actions
    loadAgentCategories,
    loadAgentsByCategory,
    loadMoreAgentsForCategory,
    selectAgentCategory,
    
    // Group actions
    loadGroupTypes,
    loadGroupsByType,
    loadMoreGroupsForType,
    selectGroupType,
    
    // Cache management
    refreshAll,
    getCacheStats,
    
    // Convenience getters
    getCurrentCommunities: () => state.communitiesByCategory[state.selectedCommunityCategory] || { items: [], hasMore: true, loading: false },
    getCurrentAgents: () => state.agentsByCategory[state.selectedAgentCategory] || { items: [], hasMore: true, loading: false },
    getCurrentGroups: () => state.groupsByType[state.selectedGroupType] || { items: [], hasMore: true, loading: false },
  };
  
  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
}

// =====================================================
// HOOK
// =====================================================

export function useCache() {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
}

// =====================================================
// SPECIALIZED HOOKS
// =====================================================

/**
 * Hook for community data with chunked loading
 */
export function useCommunities() {
  const cache = useCache();
  
  return {
    categories: cache.communityCategories,
    selectedCategory: cache.selectedCommunityCategory,
    data: cache.getCurrentCommunities(),
    loadCategories: cache.loadCommunityCategories,
    loadByCategory: cache.loadCommunitiesByCategory,
    loadMore: () => cache.loadMoreCommunitiesForCategory(cache.selectedCommunityCategory),
    selectCategory: cache.selectCommunityCategory,
  };
}

/**
 * Hook for free agent data with chunked loading
 */
export function useFreeAgents() {
  const cache = useCache();
  
  return {
    categories: cache.agentCategories,
    selectedCategory: cache.selectedAgentCategory,
    data: cache.getCurrentAgents(),
    loadCategories: cache.loadAgentCategories,
    loadByCategory: cache.loadAgentsByCategory,
    loadMore: () => cache.loadMoreAgentsForCategory(cache.selectedAgentCategory),
    selectCategory: cache.selectAgentCategory,
  };
}

/**
 * Hook for my groups data with chunked loading
 */
export function useMyGroups() {
  const cache = useCache();
  
  return {
    types: cache.groupTypes,
    selectedType: cache.selectedGroupType,
    data: cache.getCurrentGroups(),
    loadTypes: cache.loadGroupTypes,
    loadByType: cache.loadGroupsByType,
    loadMore: () => cache.loadMoreGroupsForType(cache.selectedGroupType),
    selectType: cache.selectGroupType,
  };
}

export default CacheContext;
